// Shared MAVIS conversation state — hoisted above the router so navigating
// between admin pages preserves the transcript, workflow, and pending
// proposals. All admin-facing assistant surfaces (the persistent dock and the
// legacy dashboard `AssistantPanel`) read from this single context.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Proposal } from "@/components/assistant/ProposedChangeCard";
import type { WorkflowView } from "@/components/assistant/CurrentWorkflowPanel";
import {
  serializePageContext,
  usePageContext,
  type PageContextValue,
} from "./PageContext";

export interface AssistantAction {
  label: string;
  href?: string;
  kind?: string;
}

export interface AssistantMessage {
  role: "user" | "assistant";
  content: string;
  actions?: AssistantAction[];
  proposals?: Proposal[];
  proposalMissing?: boolean;
}

const CLAIM_RE =
  /(proposed change|proposal card|confirmation card|review the proposal|approve the proposal|proposal (?:for|to)|created a proposal)/i;

const FUNCTION_NAME = "silver-sage-assistant";
const STORAGE_KEY = "eos:conversation:v1";

interface Ctx {
  messages: AssistantMessage[];
  workflow: WorkflowView | null;
  loading: boolean;
  error: string | null;
  functionName: string;
  send: (text: string) => Promise<void>;
  clear: () => void;
  retryLast: () => Promise<void>;
  setWorkflow: (w: WorkflowView | null) => void;
  /** Current live page context — the dock reads this for its breadcrumb. */
  pageContext: PageContextValue;
}

const AssistantCtx = createContext<Ctx | null>(null);

function loadPersisted(): AssistantMessage[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as AssistantMessage[];
    return [];
  } catch {
    return [];
  }
}

export function AssistantConversationProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<AssistantMessage[]>(() => loadPersisted());
  const [workflow, setWorkflow] = useState<WorkflowView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pageCtx = usePageContext();
  const { ctx: pageContext, recentActions, recordAction } = pageCtx;
  const pageContextRef = useRef(pageContext);
  const recentActionsRef = useRef(recentActions);
  useEffect(() => {
    pageContextRef.current = pageContext;
  }, [pageContext]);
  useEffect(() => {
    recentActionsRef.current = recentActions;
  }, [recentActions]);

  // Persist across hard reloads (same tab session)
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch { /* quota etc. — non-fatal */ }
  }, [messages]);

  // Restore any active workflow once on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.functions.invoke(FUNCTION_NAME, { body: { action: "workflow_get" } });
        if (data?.workflow) setWorkflow(data.workflow);
      } catch { /* no-op */ }
    })();
  }, []);

  const send = useCallback(async (text: string) => {
    const q = text.trim();
    if (!q) return;
    setError(null);
    setLoading(true);
    setMessages((prev) => {
      const next: AssistantMessage[] = [...prev, { role: "user", content: q }];
      // Fire the model call using the freshly composed history.
      (async () => {
        try {
          const { data, error: fnErr } = await supabase.functions.invoke(FUNCTION_NAME, {
            body: {
              messages: next.map((m) => ({ role: m.role, content: m.content })),
              page_context: serializePageContext(pageContextRef.current, {
                recentActions: recentActionsRef.current,
              }),
            },
          });
          if (fnErr) throw fnErr;
          const t: string = data?.text ?? "(no response)";
          const proposals: Proposal[] = data?.proposals ?? [];
          const proposalMissing = proposals.length === 0 && CLAIM_RE.test(t);
          const reply: AssistantMessage = {
            role: "assistant",
            content: t,
            actions: data?.actions ?? [],
            proposals,
            proposalMissing,
          };
          setMessages((prev2) => [...prev2, reply]);
          if (data && "workflow" in data) setWorkflow((data as { workflow?: WorkflowView | null }).workflow ?? null);
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : "Something went wrong";
          setError(msg);
        } finally {
          setLoading(false);
        }
      })();
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setMessages([]);
    setError(null);
    if (typeof window !== "undefined") {
      try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* no-op */ }
    }
  }, []);

  const retryLast = useCallback(async () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (lastUser) await send(lastUser.content);
  }, [messages, send]);

  const value: Ctx = {
    messages,
    workflow,
    loading,
    error,
    functionName: FUNCTION_NAME,
    send,
    clear,
    retryLast,
    setWorkflow,
    pageContext,
  };

  return <AssistantCtx.Provider value={value}>{children}</AssistantCtx.Provider>;
}

export function useAssistantConversation(): Ctx {
  const v = useContext(AssistantCtx);
  if (!v) {
    throw new Error("useAssistantConversation must be used inside <AssistantConversationProvider>");
  }
  return v;
}

/** Non-throwing variant for legacy call sites that may render outside the shell. */
export function useOptionalAssistantConversation(): Ctx | null {
  return useContext(AssistantCtx);
}
